<template>
  <div
    v-if="props.match"
    class="flex"
  >
    <div
      class="w-[2px] h-[120px] xl:h-[72px]"
      :class="[matchStripeColor]"
    />
    <div
      class="flex w-full gap-4"
      :class="[matchBackgroundGradient]"
    >
      <div class="flex w-full items-center ml-4 gap-4">
        <component
          :is="matchIsSuccessfullyAnalyzed ? 'router-link' : 'div'"
          :to="matchIsSuccessfullyAnalyzed ? matchLink : undefined"
          class="grid grid-cols-3 xl:flex gap-x-6 gap-y-3 xl:gap-6 flex-1"
          :class="{ 'cursor-pointer': matchIsSuccessfullyAnalyzed }"
        >
          <div class="flex flex-2 gap-3">
            <div class="flex relative">
              <img
                class="min-w-12 min-h-12 h-12 w-12 rounded-md object-cover opacity-[25%]"
                :src="`/maps/${convertMapName(props.match.match.map_name)}_cs2.jpg`"
              />
              <img
                class="w-8 h-8 rounded-md object-cover absolute top-0 left-0 translate-x-1/4 translate-y-1/4"
                :src="`/maps/map_icon_${convertMapName(props.match.match.map_name)}.svg`"
                alt=""
              />
              <div
                v-if="props.match.match.invalid_match"
                class="absolute right-0 bottom-0 rounded-md bg-warning-900 text-warning-400 px-1 py-1"
                :title="$t('coach.match_header.invalidated')"
              >
                <ExclamationTriangleIcon class="w-3 h-3" />
              </div>
            </div>

            <div class="flex flex-col gap-1">
              <div class="text-text-primary font-medium flex gap-1">
                <span :class="[outcome === 'won' ? 'text-success-400' : '']">{{
                  getUserTeamScore()
                }}</span>
                -
                <span :class="[outcome === 'lost' ? 'text-danger-400' : '']">{{
                  getOpponentTeamScore()
                }}</span>
              </div>
              <div class="flex gap-1">
                <img
                  class="w-4 h-4 object-cover"
                  :src="`/img/icons/${props.match.match.platform}_icon.png`"
                />
                <h2 class="text-text-secondary text-xs">
                  {{ convertMatchTypeName(props.match.match.match_type) }}
                </h2>
              </div>
            </div>
          </div>

          <div v-if="!matchIsAnalyzing && !matchIsPermanentlyFailed && !matchIsRetryable" class="flex-1 flex-col flex gap-1 justify-center">
            <h1 class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('coach.match_row.rank') }}</h1>
            <h2 class="text-text-primary text-sm flex items-center justify-start">
              <Rank
                v-if="props.match.overview.end_rank"
                :rank="props.match.overview.end_rank"
                :platform="props.match.match.platform"
                :matchType="props.match.match.match_type"
                :cs_map="props.match.match.map_name"
              />
              <div v-else>
                -
              </div>
              <div v-if="props.match.overview.start_rank">
                <span
                  v-if="props.match.overview.end_rank - props.match.overview.start_rank > 1"
                  class="text-xs pl-1 text-success-400"
                >
                  +{{ props.match.overview.end_rank - props.match.overview.start_rank }}
                </span>
                <span
                  v-else-if="props.match.overview.start_rank - props.match.overview.end_rank > 1"
                  class="text-xs pl-1 text-danger-400"
                >
                  -{{ props.match.overview.start_rank - props.match.overview.end_rank }}
                </span>
                <ArrowUpIcon
                  v-else-if="props.match.overview.end_rank - props.match.overview.start_rank == 1"
                  class="w-4 h-4 text-success-400 ml-1"
                />
                <ArrowDownIcon
                  v-else-if="props.match.overview.start_rank - props.match.overview.end_rank == 1"
                  class="w-4 h-4 text-danger-400 ml-1"
                />
              </div>
            </h2>
          </div>

          <div v-else class="flex-1 flex-col flex gap-1 justify-center">
            <h1 class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('coach.match_row.status') }}</h1>
            <h2
              :class="[matchStatusTextColor]"
              class="text-sm"
            >
              {{ statusText }}
            </h2>
          </div>

          <div class="flex-1 flex-col flex gap-1 justify-center">
            <h1 class="text-text-secondary text-xs uppercase tracking-[1.5px]">{{ $t('common.date') }}</h1>
            <h2 class="text-text-primary text-sm">
              {{
                props.match.match.played_on || props.match.match.date
                  ? moment(props.match.match.played_on || props.match.match.date).format(
                    "MMM Do YYYY",
                  )
                  : "-"
              }}
            </h2>
          </div>
          <div class="flex-1 flex-col flex gap-1 justify-center">
            <h1 class="text-text-secondary text-xs uppercase tracking-[1.5px]">
              K / D / A
            </h1>
            <h2 class="text-text-primary text-sm">
              {{ props.match.overview.kills == null ? "?" : props.match.overview.kills }} /
              {{ props.match.overview.deaths == null ? "?" : props.match.overview.deaths }} /
              {{ props.match.overview.assists == null ? "?" : props.match.overview.assists }}
            </h2>
          </div>
          <div class="flex-1 flex-col flex gap-1 justify-center">
            <h1 class="text-text-secondary text-xs uppercase tracking-[1.5px]">
              {{ $t('coach.stat_labels.headshot_kill_percentage') }}
            </h1>
            <h2 class="text-text-primary text-sm">
              {{
                props.match.overview.kills == null || props.match.overview.headshot_kills == null
                  ? "?"
                  : props.match.overview.kills === 0
                    ? "0"
                    : ((props.match.overview.headshot_kills / props.match.overview.kills) * 100).toFixed(0)
              }}%
            </h2>
          </div>
          <div class="flex-1 flex-col flex gap-1 justify-center">
            <h1 class="text-text-secondary text-xs uppercase tracking-[1.5px]">KPR</h1>
            <h2 class="text-text-primary text-sm">
              {{
                props.match.overview.kpr == null
                  ? "?"
                  : props.match.overview.kpr.toFixed(2)
              }}
            </h2>
          </div>
        </component>
        <div class="flex flex-col gap-1 justify-start items-start">
          <MatchRowButton
            :match="props.match"
            :matchIsRetryable="matchIsRetryable"
            :matchIsPermanentlyFailed="matchIsPermanentlyFailed"
            :matchIsSuccessfullyAnalyzed="matchIsSuccessfullyAnalyzed"
            :matchIsAnalyzing="matchIsAnalyzing"
            :goToMatchPage="goToMatchPage"
            :retryMatch="retryMatch"
          />
        </div>
      </div>
    </div>
  </div>
  <div v-else />
</template>

<script setup>
import moment from 'moment';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { computed } from 'vue';
import { ArrowUpIcon, ArrowDownIcon, ExclamationTriangleIcon } from '@heroicons/vue/24/outline';
import axios from 'axios';
import { useStore } from 'vuex';
import MatchRowButton from '../../../Matches/MatchRow/MatchRowButton.vue';
import Rank from '../../../Matches/display/Rank.vue';

const store = useStore();
const router = useRouter();
const { t } = useI18n();
// const emit = defineEmits(["toggleAllstar"]); // Allstar highlights disabled

const props = defineProps({
  match: {
    type: Object,
    required: true,
  },
});

// `outcome` is a sibling of `match` in the recent_match_stats entry
// ({ match, overview, outcome }), not a field on the match itself.
const outcome = computed(() => props.match.outcome);

const matchIsRetryable = computed(() => {
  const retryableStatuses = ['rts_failed', 'parsing_failed', 'calculation_failed', 'analysis_failed', 'download_failed', 'expired'];
  return retryableStatuses.includes(props.match.match.status);
});

const matchIsPermanentlyFailed = computed(() => {
  const permanentlyFailedStatuses = ['expired', 'demo_not_available'];
  return permanentlyFailedStatuses.includes(props.match.match.status);
});

const matchIsSuccessfullyAnalyzed = computed(() => {
  const successfullyAnalyzedStatuses = ['analyzed'];
  return successfullyAnalyzedStatuses.includes(props.match.match.status);
});

const matchIsAnalyzing = computed(() => {
  const analyzingStatuses = ['queued_for_analysis', 'temporarily_failed'];
  return analyzingStatuses.includes(props.match.match.status);
});

const statusText = computed(() => {
  if (matchIsAnalyzing.value) {
    return t('coach.match_row.analyzing');
  }
  if (matchIsPermanentlyFailed.value) {
    return t('coach.match_row.parsing_failed');
  }
  return t('coach.match_row.parsing_failed');
});

const matchStripeColor = computed(() => {
  if (props.match.match.status === 'queued_for_analysis') {
    return 'bg-warning-300';
  }
  if (props.match.match.status === 'download_failed') {
    return 'bg-danger-400';
  }
  if (outcome.value === 'tied') {
    return 'bg-gray-600';
  }
  return outcome.value === 'won'
    ? 'bg-success-400'
    : 'bg-danger-400';
});

const matchStatusTextColor = computed(() => {
  if (matchIsAnalyzing.value) {
    return 'text-warning-300';
  }

  return 'text-danger-400';
});

const matchBackgroundGradient = computed(() => {
  if (matchIsAnalyzing.value || matchIsPermanentlyFailed.value) {
    return '';
  }
  if (outcome.value === 'tied') {
    return '';
  }
  return outcome.value === 'won'
    ? 'bg-success-gradient'
    : 'bg-danger-gradient';
});

const matchScore = computed(() => {
  // Null-check, not falsy-check: 0 is a real score.
  const scores = props.match?.match;
  if (scores?.a_score == null || scores?.b_score == null) return [0, 0];

  return [scores.a_score, scores.b_score];
});

const matchLink = computed(() => ({
  name: 'MatchPage',
  params: { id: props.match.match.uuid || props.match.match_id },
}));

function goToMatchPage() {
  if (!matchIsSuccessfullyAnalyzed.value) {
    return;
  }

  router.push(matchLink.value);
}

function retryMatch() {
  axios.post(`/matches/${props.match.match.uuid || props.match.match_id}/request_analysis`).then(() => {
    store.commit('showMessage', { messageType: 'success', messageText: t('coach.match_row.retry_requested') });
  }).catch(() => {
    store.commit('showMessage', { messageType: 'error', messageText: t('coach.match_row.retry_failed') });
  }).finally(() => {
    setTimeout(() => {
      store.commit('hideMessage');
    }, 3000);
  });
}

function convertMapName(name) {
  return name?.includes('wm_') ? name.replace('wm_', 'de_') : name;
}

function convertMatchTypeName(name) {
  // Capitalize first letter
  return name?.charAt(0).toUpperCase() + name?.slice(1) || t('coach.match_row.unknown_match_type');
}

function getUserTeamScore() {
  if (!matchScore.value) return 0;

  if (props.match.match.status === 'queued_for_analysis' || props.match.match.status === 'download_failed') {
    if (outcome.value === 'won') {
      return Math.max(matchScore.value[0], matchScore.value[1]);
    }
    return Math.min(matchScore.value[0], matchScore.value[1]);
  }

  if (outcome.value === 'won') {
    return Math.max(matchScore.value[0], matchScore.value[1]);
  }
  return Math.min(matchScore.value[0], matchScore.value[1]);
}

function getOpponentTeamScore() {
  if (!matchScore.value) return 0;

  if (props.match.match.status === 'queued_for_analysis' || props.match.match.status === 'download_failed') {
    if (outcome.value === 'lost') {
      return Math.max(matchScore.value[0], matchScore.value[1]);
    }
    return Math.min(matchScore.value[0], matchScore.value[1]);
  }

  if (outcome.value === 'won') {
    return Math.min(matchScore.value[0], matchScore.value[1]);
  }
  return Math.max(matchScore.value[0], matchScore.value[1]);
}
</script>

<style scoped>
.bg-success-gradient {
  background: linear-gradient(90deg, rgba(10, 72, 64, 0.2) 0%, rgba(10, 72, 64, 0) 100%);
}
.bg-danger-gradient {
  background: linear-gradient(
    90deg,
    rgba(103, 19, 19, 0.2) 0%,
    rgba(103, 19, 19, 0) 100%
  );
}
</style>
